import React, { useState } from 'react';
import {
  Button,
  TextInput,
  Tag,
  InlineNotification,
} from '@carbon/react';
import { Add, Star, StarFilled, TrashCan, ImageReference } from '@carbon/react/icons';

import {
  useGetHolidayVariantsQuery,
  useCreateHolidayVariantMutation,
  useUpdateHolidayVariantMutation,
  useDeleteHolidayVariantMutation,
  useSetHolidayVariantDefaultMutation,
} from '@/redux/new_services/holidayVariants';

import HolidayMediaSlot from './HolidayMediaSlot';
import './HolidayVariantsEditor.scss';

// HolidayVariantsEditor — pro Holiday eine Liste von Medien-Varianten,
// jeweils mit landscape + portrait Slot und einer Default-Markierung.
// Layout: jede Variante als eigenständige Card mit Header (Name + Aktionen)
// und Slot-Grid darunter, sodass der Admin sofort sieht welche Variante
// welches Bildpaar hat.
const HolidayVariantsEditor = ({ holidayId }) => {
  const { data: variants = [], isLoading } = useGetHolidayVariantsQuery(holidayId, {
    skip: !holidayId,
  });
  const [createVariant] = useCreateHolidayVariantMutation();
  const [updateVariant] = useUpdateHolidayVariantMutation();
  const [deleteVariant] = useDeleteHolidayVariantMutation();
  const [setDefault] = useSetHolidayVariantDefaultMutation();

  const [newName, setNewName] = useState('');
  const [error, setError] = useState(null);

  if (!holidayId) {
    return (
      <InlineNotification
        kind="info"
        title="Hinweis"
        subtitle="Speichere den Feiertag, bevor du Varianten anlegst."
        hideCloseButton
        lowContrast
      />
    );
  }

  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      await createVariant({
        holidayId,
        name: newName.trim(),
        is_default: variants.length === 0, // erste Variante ist Default
      }).unwrap();
      setNewName('');
      setError(null);
    } catch (e) {
      setError('Variante konnte nicht angelegt werden');
    }
  };

  const handleRename = async (variant, name) => {
    if (name === variant.name) return;
    try {
      await updateVariant({
        id: variant.model.ID,
        holidayId,
        name,
        is_default: variant.is_default,
        landscape_media_id: variant.landscape_media_id,
        portrait_media_id: variant.portrait_media_id,
      }).unwrap();
    } catch (e) {
      setError('Umbenennen fehlgeschlagen');
    }
  };

  const handleMediaChange = async (variant, key, newMediaId) => {
    try {
      await updateVariant({
        id: variant.model.ID,
        holidayId,
        name: variant.name,
        is_default: variant.is_default,
        landscape_media_id: key === 'landscape' ? newMediaId : variant.landscape_media_id,
        portrait_media_id: key === 'portrait' ? newMediaId : variant.portrait_media_id,
      }).unwrap();
    } catch (e) {
      setError('Medien-Zuordnung fehlgeschlagen');
    }
  };

  const handleSetDefault = async (variant) => {
    try {
      await setDefault({ id: variant.model.ID, holidayId }).unwrap();
    } catch (e) {
      setError('Default-Markierung fehlgeschlagen');
    }
  };

  const handleDelete = async (variant) => {
    if (!confirm(`Variante "${variant.name}" wirklich löschen?`)) return;
    try {
      await deleteVariant({ id: variant.model.ID, holidayId }).unwrap();
    } catch (e) {
      setError('Variante konnte nicht gelöscht werden');
    }
  };

  return (
    <section className="hve">
      <header className="hve__head">
        <ImageReference size={20} />
        <h4 className="hve__title">Medien-Varianten</h4>
        <span className="hve__count">{variants.length}</span>
      </header>

      {error && (
        <InlineNotification
          kind="error"
          title="Fehler"
          subtitle={error}
          onCloseButtonClick={() => setError(null)}
          lowContrast
        />
      )}

      {!isLoading && variants.length === 0 && (
        <p className="hve__hint">
          Noch keine Variante. Lege eine an, um Hintergrundmedien hochzuladen.
          Eine Variante besteht aus einem Hochkant- und einem Querformat-Medium.
        </p>
      )}

      <div className="hve__list">
        {variants.map((variant) => (
          <article key={variant.model.ID} className={`hve-card ${variant.is_default ? 'hve-card--default' : ''}`}>
            <header className="hve-card__head">
              <div className="hve-card__name">
                <TextInput
                  id={`variant-name-${variant.model.ID}`}
                  labelText={`Name der Variante`}
                  hideLabel
                  defaultValue={variant.name}
                  onBlur={(e) => handleRename(variant, e.target.value.trim())}
                  size="md"
                />
              </div>
              <div className="hve-card__actions">
                {variant.is_default ? (
                  <Tag type="green" renderIcon={StarFilled}>Standard</Tag>
                ) : (
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={Star}
                    onClick={() => handleSetDefault(variant)}
                  >
                    Als Standard
                  </Button>
                )}
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={TrashCan}
                  hasIconOnly
                  iconDescription="Variante löschen"
                  onClick={() => handleDelete(variant)}
                />
              </div>
            </header>

            <div className="hve-card__slots">
              <div className="hve-card__slot">
                <div className="hve-card__slot-label">Querformat</div>
                <HolidayMediaSlot
                  mediaId={variant.landscape_media_id}
                  orientation="landscape"
                  onChange={(newId) => handleMediaChange(variant, 'landscape', newId)}
                />
              </div>
              <div className="hve-card__slot">
                <div className="hve-card__slot-label">Hochformat</div>
                <HolidayMediaSlot
                  mediaId={variant.portrait_media_id}
                  orientation="portrait"
                  onChange={(newId) => handleMediaChange(variant, 'portrait', newId)}
                />
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="hve__add">
        <TextInput
          id="new-variant-name"
          labelText="Name der neuen Variante"
          placeholder="z. B. Festlich Klassisch"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
        />
        <Button kind="secondary" renderIcon={Add} onClick={handleAdd} disabled={!newName.trim()}>
          Variante hinzufügen
        </Button>
      </div>
    </section>
  );
};

export default HolidayVariantsEditor;
